import React, { useState, useEffect } from "react";
import { ConfigProvider, theme, message, Spin, Grid, Modal } from "antd";
import FloatingToolbar from "./components/FloatingToolbar";
import ImageGallery from "./components/ImageGallery";
import LoginRegister from "./components/LoginRegister";
import LogoWithText from "./components/LogoWithText";
import api from "./utils/api";
import ApiDocs from "./components/ApiDocs";
import MapPage from "./components/MapPage";
import ShareView from "./components/ShareView";
import DirectorySelector from "./components/DirectorySelector";
import { getToken, getUser, clearAll } from "./utils/secureStorage";

function App() {
  const [currentTheme, setCurrentTheme] = useState("light");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [userRole, setUserRole] = useState(null);
  const [authState, setAuthState] = useState(0); // Track auth state changes
  
  // Batch Mode State
  const [isBatchMode, setIsBatchMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());

  // Batch Move State
  const [moveModalVisible, setMoveModalVisible] = useState(false);
  const [targetMoveDir, setTargetMoveDir] = useState("");
  const [moving, setMoving] = useState(false);

  // Simple router check
  const isApiDocs = window.location.pathname === "/opendocs";
  const isMapPage = window.location.pathname === "/map";
  const isShareView = window.location.pathname.startsWith("/share");
  
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme) {
      setCurrentTheme(savedTheme);
    }
  }, []);

  const handleThemeChange = (theme) => {
    setCurrentTheme(theme);
    localStorage.setItem("theme", theme);
  };

  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        setAuthLoading(true);
        
        // Check if we have a valid token and user info
        const token = getToken();
        const user = getUser();
        
        if (token && user) {
          // Verify token is still valid
          try {
            const response = await api.get("/auth/me");
            if (response.data.success) {
              setIsAuthenticated(true);
              setPasswordRequired(false); // No password required with JWT
              setUserRole(response.data.user.role);
            } else {
              // Token invalid, clear auth data
              clearAll();
              setPasswordRequired(true);
              setUserRole(null);
            }
          } catch (error) {
            // Token invalid, clear auth data
            clearAll();
            setPasswordRequired(true);
            setUserRole(null);
          }
        } else {
          // No token, check if password is required (legacy auth)
          try {
            const response = await api.get("/auth/status");
            const data = response.data;
            
            if (data.requiresPassword) {
              setPasswordRequired(true);
            } else {
              setIsAuthenticated(true);
              setPasswordRequired(false);
            }
          } catch (error) {
            console.error("Auth status check failed:", error);
            setPasswordRequired(true);
          }
        }
      } catch (error) {
        console.error("Auth check failed:", error);
        setPasswordRequired(true);
      } finally {
        setAuthLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    setPasswordRequired(false);
    message.success("欢迎回来");
  };

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const toggleBatchMode = () => {
    setIsBatchMode(prev => !prev);
    setSelectedItems(new Set());
  };

  const handleSelectionChange = (newSelection) => {
    setSelectedItems(newSelection);
  };

  const handleBatchDelete = async () => {
    if (selectedItems.size === 0) return;
    
    try {
      const hide = message.loading("正在删除...", 0);
      // Execute deletions in parallel
      const promises = Array.from(selectedItems).map(relPath => 
        api.delete(`/images/${encodeURIComponent(relPath)}`)
      );
      
      await Promise.all(promises);
      hide();
      message.success(`成功删除 ${selectedItems.size} 张图片`);
      
      // Reset state
      setSelectedItems(new Set());
      setIsBatchMode(false);
      handleRefresh();
    } catch (error) {
      console.error("Batch delete error:", error);
      const errorMsg = error.response?.data?.error || "部分图片删除失败，请重试";
      message.error(errorMsg);
      handleRefresh(); // Refresh anyway to show what's left
    }
  };

  const handleBatchMove = () => {
    if (selectedItems.size === 0) return;
    setTargetMoveDir(""); // Reset
    setMoveModalVisible(true);
  };

  const confirmBatchMove = async () => {
    if (selectedItems.size === 0) return;
    
    setMoving(true);
    try {
        const res = await api.post("/batch/move", {
            files: Array.from(selectedItems),
            targetDir: targetMoveDir
        });
        
        if (res.data.success) {
            message.success(res.data.message);
            setMoveModalVisible(false);
            setSelectedItems(new Set());
            setIsBatchMode(false);
            handleRefresh();
        } else {
            message.error(res.data.error || "移动失败");
        }
    } catch (e) {
        message.error("移动失败，请重试");
    } finally {
        setMoving(false);
    }
  };

  // Global styles for glassmorphism and background
  const globalStyles = `
    body {
      margin: 0;
      padding: 0;
      background: ${currentTheme === 'dark' ? '#0f0f0f' : '#f5f7fa'};
      color: ${currentTheme === 'dark' ? '#fff' : '#000'};
      transition: background 0.3s ease, color 0.3s ease;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    }
    
    /* Custom Scrollbar */
    ::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }
    ::-webkit-scrollbar-track {
      background: transparent;
    }
    ::-webkit-scrollbar-thumb {
      background: ${currentTheme === 'dark' ? '#333' : '#ccc'};
      border-radius: 4px;
    }
    ::-webkit-scrollbar-thumb:hover {
      background: ${currentTheme === 'dark' ? '#555' : '#999'};
    }
    
    /* Prevent dropdown scroll from affecting main page */
    .directory-selector-dropdown .rc-virtual-list-holder {
      overflow-y: auto !important;
      overscroll-behavior: contain;
    }
  `;

  return (
    <ConfigProvider
      theme={{
        algorithm: currentTheme === "dark" ? theme.darkAlgorithm : theme.defaultAlgorithm,
        token: {
          colorPrimary: "#1677ff",
          borderRadius: 12,
          colorText: currentTheme === "dark" ? "#ffffff" : "#000000",
        },
      }}
    >
      <style>{globalStyles}</style>

      {/* Main Content */}
      <div style={{ position: "relative", minHeight: "100vh" }}>
        {isApiDocs ? (
            <ApiDocs />
        ) : isMapPage ? (
            <MapPage />
        ) : isShareView ? (
            <ShareView currentTheme={currentTheme} onThemeChange={handleThemeChange} />
        ) : authLoading ? (
           <div style={{ 
               display: "flex", 
               justifyContent: "center", 
               alignItems: "center", 
               height: "100vh",
               flexDirection: "column",
               gap: 20
           }}>
             <LogoWithText />
             <Spin size="large" />
           </div>
        ) : passwordRequired && !isAuthenticated ? (
          /* Only render LoginRegister when authentication is required and not authenticated */
          <LoginRegister 
            onLoginSuccess={handleLoginSuccess} 
            isMobile={isMobile}
          />
        ) : (
          /* Render main content when authenticated or no password required */
          <>
            {/* Waterfall Gallery */}
            <ImageGallery 
              api={api} 
              onRefresh={handleRefresh}
              refreshTrigger={refreshTrigger}
              isAuthenticated={!passwordRequired || isAuthenticated}
              isBatchMode={isBatchMode}
              selectedItems={selectedItems}
              onSelectionChange={handleSelectionChange}
            />

            {/* Floating Toolbar */}
            <FloatingToolbar 
              onThemeChange={handleThemeChange}
              currentTheme={currentTheme}
              onRefresh={handleRefresh}
              api={api}
              isMobile={isMobile}
              isBatchMode={isBatchMode}
              toggleBatchMode={toggleBatchMode}
              selectedCount={selectedItems.size}
              onBatchDelete={handleBatchDelete}
              onBatchMove={handleBatchMove}
              userRole={userRole}
            />

            {/* Batch Move Modal */}
            <Modal
                open={moveModalVisible}
                title="移动到..."
                onCancel={() => setMoveModalVisible(false)}
                onOk={confirmBatchMove}
                confirmLoading={moving}
                okText="确认移动"
                cancelText="取消"
                destroyOnClose
            >
                <div style={{ padding: "20px 0" }}>
                    <p style={{ marginBottom: 12 }}>将选中的 {selectedItems.size} 张图片移动到：</p>
                    <DirectorySelector 
                        value={targetMoveDir}
                        onChange={setTargetMoveDir}
                        api={api}
                        allowInput={true}
                        placeholder="选择或输入目标相册"
                    />
                </div>
            </Modal>
          </>
        )}
      </div>
    </ConfigProvider>
  );
}

export default App;
